import React, { useState, useCallback, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Hero from '../components/Hero';
import GameTicker from '../components/GameTicker';
import NextGameBanner from '../components/NextGameBanner';
import RecentResults from '../components/RecentResults';
import GallerySlideshow from '../components/GallerySlideshow';
import RosterPreview from '../components/RosterPreview';
import AboutSection from '../components/AboutSection';
import ContactSection from '../components/ContactSection';
import SponsorsStrip from '../components/SponsorsStrip';
import Footer from '../components/Footer';
import IntroAnimation from '../components/IntroAnimation';
import { getImageUrl } from '../lib/api';

// Preload critical images before anything renders
function usePreloadAssets() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const logoUrl = getImageUrl('/api/public/club-logo');
    const heroUrl = getImageUrl('/api/public/hero-bg');

    const loadImage = (src) => new Promise((resolve) => {
      const img = new Image();
      img.onload = resolve;
      img.onerror = resolve; // resolve even on error so we don't hang forever
      img.src = src;
    });

    Promise.all([loadImage(logoUrl), loadImage(heroUrl)]).then(() => setReady(true));

    // Safety timeout: start after 4 seconds regardless
    const timeout = setTimeout(() => setReady(true), 4000);
    return () => clearTimeout(timeout);
  }, []);

  return ready;
}

export default function HomePage() {
  const assetsReady = usePreloadAssets();
  const [introComplete, setIntroComplete] = useState(
    sessionStorage.getItem('devils_intro_seen') === 'true'
  );

  const handleIntroComplete = useCallback(() => {
    setIntroComplete(true);
    sessionStorage.setItem('devils_intro_seen', 'true');
  }, []);

  // Show loading spinner until assets loaded
  if (!assetsReady) {
    return (
      <div className="fixed inset-0 bg-[#020203] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-white/10 border-t-[#CC0000] rounded-full animate-spin" />
          <span className="text-xs text-white/20 tracking-[0.2em] uppercase" style={{ fontFamily: 'Space Grotesk' }}>Laden...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(0,0%,5%)]">
      {!introComplete && <IntroAnimation onComplete={handleIntroComplete} />}
      <Navbar />
      <Hero />
      <GameTicker />
      <NextGameBanner />
      
      {/* Recent Results */}
      <section id="spiele" className="py-16 sm:py-20 lg:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <span className="text-xs text-[#46C7FF] uppercase tracking-[0.2em] font-semibold">Ergebnisse</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mt-3" style={{ fontFamily: 'Space Grotesk' }}>Letzte Spiele</h2>
          </div>
          <RecentResults />
        </div>
      </section>

      {/* Gallery Slideshow */}
      <GallerySlideshow />

      {/* Roster Preview */}
      <section className="py-16 sm:py-20 lg:py-28 relative">
        <div className="absolute inset-0 bg-[hsl(0,0%,4%)]" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <span className="text-xs text-[#46C7FF] uppercase tracking-[0.2em] font-semibold">Mannschaft</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mt-3" style={{ fontFamily: 'Space Grotesk' }}>Unser Kader</h2>
          </div>
          <RosterPreview />
        </div>
      </section>

      <AboutSection />
      <ContactSection />
      <SponsorsStrip />
      <Footer />
    </div>
  );
}
